import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import s from './ProfileHeader.module.css';
import { appAxiosInstance, assistantAxiosInstance } from "../../../api";
import { useParams } from 'react-router-dom';

interface UserInfoResponse {
    name: string;
    email: string;
    linkPhoto: string;
    colorType: string;
    typeFigure: string;
    height: number;
    shoulderWidth: number;
    chestCircumference: number;
    waistCircumference: number;
    hipCircumference: number;
    gender: string;
    dateStart: string;
}

interface PersonalCapsuleParametersResponse {
    comment: string;
    events: string[];
    createdAt: string;
}

interface StyleResponse {
    [key: string]: number;
}

interface TopStyle {
    label: string;
    score: number;
}

const ProfileHeader: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const [user, setUser] = useState<UserInfoResponse | null>(null);
    const [topStyles, setTopStyles] = useState<TopStyle[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showPhotoModal, setShowPhotoModal] = useState<boolean>(false);

    const [capsuleParams, setCapsuleParams] = useState<PersonalCapsuleParametersResponse | null>(null);

    useEffect(() => {

        if (!userId) {
            setError('ID пользователя не указан');
            setLoading(false);
            return;
        }

        const userIdNum = Number(userId);
        if (isNaN(userIdNum) || userIdNum <= 0) {
            setError('Некорректный ID пользователя');
            setLoading(false);
            return;
        }

        fetchUserData(userIdNum);
    }, [userId]);

    const formatDate = (dateString: string) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const fetchUserData = async (id: number) => {
        setLoading(true);
        setError(null);
        try {
            const [userResponse, stylesResponse, capsuleParamsResponse] = await Promise.all([
                appAxiosInstance.get<UserInfoResponse>(
                    `/backend/getUserInfoForPersonalCapsule/${id}`
                ),
                assistantAxiosInstance.get<StyleResponse>(
                    `fashion/getMapStylesInFashionsUser?userId=${id}`
                ),
                assistantAxiosInstance.get<PersonalCapsuleParametersResponse>(
                    `/mobile/assistant/admin/personalCapsule/parameters/${id}`
                ),
            ]);

            setUser(userResponse.data);
            setCapsuleParams(capsuleParamsResponse.data);

            if (stylesResponse.data && Object.keys(stylesResponse.data).length > 0) {
                const sortedStyles = Object.entries(stylesResponse.data)
                    .sort(([, a], [, b]) => (b as number) - (a as number))
                    .slice(0, 3)
                    .map(([label, score]) => ({ label, score: score as number }));
                setTopStyles(sortedStyles);
            } else {
                setTopStyles([]);
            }
        } catch (err) {
            setError('Ошибка при загрузке данных пользователя');
        } finally {
            setLoading(false);
        }
    };

    const handlePhotoClick = () => {
        if (user?.linkPhoto) {
            setShowPhotoModal(true);
        }
    };

    if (loading) {
        return <div className={s.loading}>Загрузка...</div>;
    }

    if (error) {
        return <div className={s.error}>{error}</div>;
    }

    if (!user) {
        return <div className={s.error}>Пользователь не найден</div>;
    }

    return (
        <>
            <div className={s.container}>
                <div className={s.identityCard}>
                    <div className={s.avatarWrapper}>
                        {user.linkPhoto ? (
                            <img
                                src={user.linkPhoto}
                                alt={user.name}
                                className={s.avatar}
                                onClick={handlePhotoClick}
                            />
                        ) : (
                            <div className={s.avatarPlaceholder}>
                                {user.name?.charAt(0) || '?'}
                            </div>
                        )}
                    </div>
                    <div className={s.userInfo}>
                        <h1 className={s.name}>{user.name || '—'}</h1>
                        <p className={s.email}>{user.email || '—'}</p>
                        <p className={s.colorTypeLabel}>{user.colorType || '—'}</p>
                        <p className={s.typeFigureLabel}>Тип фигуры: {user.typeFigure || '—'}</p>
                        <p className={s.typeFigureLabel}>Дата оформления подписки: { formatDate(user.dateStart || '—')}</p>
                    </div>
                </div>

                <div className={s.rightColumn}>
                    <div className={s.metrics}>
                        <div className={s.metricCard}>
                            <span className={s.metricLabel}>Рост</span>
                            <span className={s.metricValue}>
                                {user.height || 0} <span className={s.metricHint}>см</span>
                            </span>
                        </div>
                        <div className={s.metricCard}>
                            <span className={s.metricLabel}>Плечи</span>
                            <span className={s.metricValue}>
                                {user.shoulderWidth || 0} <span className={s.metricHint}>см</span>
                            </span>
                        </div>
                        <div className={s.metricCard}>
                            <span className={s.metricLabel}>Грудь</span>
                            <span className={s.metricValue}>
                                {user.chestCircumference || 0} <span className={s.metricHint}>см</span>
                            </span>
                        </div>
                        <div className={s.metricCard}>
                            <span className={s.metricLabel}>Талия</span>
                            <span className={s.metricValue}>
                                {user.waistCircumference || 0} <span className={s.metricHint}>см</span>
                            </span>
                        </div>
                        <div className={s.metricCard}>
                            <span className={s.metricLabel}>Бёдра</span>
                            <span className={s.metricValue}>
                                {user.hipCircumference || 0} <span className={s.metricHint}>см</span>
                            </span>
                        </div>
                    </div>

                    <div className={s.topStyles}>
                        <span className={s.stylesLabel}>Топ стилей</span>
                        <div className={s.stylesList}>
                            {topStyles.length > 0 && (
                                topStyles.map((style) => (
                                    <div key={style.label} className={s.styleItem}>
                                        <span className={s.styleScore}>{style.score}</span>
                                        <span className={s.styleName}>{style.label}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className={s.capsuleCard}>
                <div className={s.capsuleHeader}>
                    <span className={s.capsuleTitle}>Запрос пользователя</span>
                    {capsuleParams?.createdAt && (
                        <span className={s.capsuleDate}>
                {formatDate(capsuleParams.createdAt)}
            </span>
                    )}
                </div>

                {capsuleParams?.comment && (
                    <p className={s.capsuleComment}>{capsuleParams.comment}</p>
                )}

                {capsuleParams?.events && capsuleParams.events.length > 0 ? (
                    <div className={s.capsuleChips}>
                        {capsuleParams.events.map((event, index) => (
                            <span key={index} className={s.capsuleChip}>
                    {event}
                </span>
                        ))}
                    </div>
                ) : (
                    <span className={s.emptyStyles}>Запрос не указан</span>
                )}
            </div>

            <Modal
                show={showPhotoModal}
                onHide={() => setShowPhotoModal(false)}
                centered
                size="lg"
                className={s.photoModal}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Фото пользователя</Modal.Title>
                </Modal.Header>
                <Modal.Body className={s.photoModalBody}>
                    <div className={s.photoModalContainer}>
                        <img
                            src={user.linkPhoto}
                            alt={user.name}
                            className={s.photoModalImage}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className={s.photoModalCloseBtn}
                        onClick={() => setShowPhotoModal(false)}
                    >
                        Закрыть
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ProfileHeader;